<script setup>
import { ref, computed, onMounted, getCurrentInstance } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useIsMobile } from "@/shared/lib/composables/useIsMobile";
import { useI18n } from "vue-i18n";

import ClientDocumentSignFlow from "@/pages/admin/client_cabinet/components/mobile/ClientDocumentSignFlow.vue";
import Button from "@/shared/ui/button/Button.vue";
import {
  getCurrentSignParticipant,
  signParticipant,
  signatureValidate,
} from "@/entities/transaction";
import nclayerPort from "@/shared/nclayer";

import {
  getClientIP,
  generateDeviceFingerprint,
  getUserAgent,
} from "@/shared/lib/signatureHelpers";

const props = defineProps({
  consentChecked: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["signed", "close"]);

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { proxy } = getCurrentInstance();

const signMethod = ref("eds");
const showSignFlow = ref(false);
const { isMobile } = useIsMobile();
const isSubmitting = ref(false);
const isSuccessSign = ref(false);
const signError = ref("");
const currentSignerData = ref(null);
const currentSigner = ref(null);
const isLoadingSigner = ref(false);
const participants = ref([]);
const signaturesInfo = ref({
  signed: 0,
  remaining: 0,
  total: 0,
});

const transaction_id = computed(() => route.params.id);
const participantUuid = computed(() => route.query.participant_uuid || null);

const canCurrentUserSign = computed(() => {
  if (!currentSigner.value || !participantUuid.value) return false;
  return currentSigner.value.uuid === participantUuid.value;
});

const currentParticipantOrder = computed(() => currentSigner.value?.order || 0);

const signaturesStatusText = computed(() => {
  const { signed, remaining, total: totalCount } = signaturesInfo.value;
  return `${t("Подписано")}: ${signed}/${totalCount}, ${t(
    "Осталось"
  )}: ${remaining}, ${t("Очередь")}: ${currentParticipantOrder.value}`;
});

const allSigned = computed(() => {
  return (
    !currentSigner.value &&
    signaturesInfo.value.remaining === 0 &&
    signaturesInfo.value.total > 0
  );
});

const myParticipant = computed(() => {
  if (!participantUuid.value || !participants.value.length) return null;
  return participants.value.find((p) => p.uuid === participantUuid.value);
});

const hasUserAlreadySigned = computed(() => {
  if (myParticipant.value?.status === "SIGNED") return true;
  return allSigned.value;
});

async function loadCurrentSigner() {
  if (!transaction_id.value) return;
  isLoadingSigner.value = true;
  try {
    const data = await getCurrentSignParticipant(transaction_id.value);
    if (data.success && data.payload) {
      currentSignerData.value = data.payload;
      currentSigner.value = data.payload.current_participant;
      signaturesInfo.value = {
        signed: data.payload.signatures?.signed || 0,
        remaining: data.payload.signatures?.remaining || 0,
        total: data.payload.signatures?.total || 0,
      };
      participants.value = data.payload.all_participants || [];
    }
  } catch (error) {
    console.error("Ошибка загрузки текущего подписанта:", error);
    currentSigner.value = null;
  } finally {
    isLoadingSigner.value = false;
  }
}

function validateSigningOrder() {
  if (!currentSigner.value) {
    if (allSigned.value) {
      throw new Error(t("Все участники уже подписали документ"));
    }
    throw new Error(t("Не удалось определить очередь подписания"));
  }
  if (!participantUuid.value) {
    throw new Error(t("Не найден UUID участника"));
  }
  if (!canCurrentUserSign.value) {
    const name = currentSigner.value.last_name
      ? `${currentSigner.value.last_name} ${
          currentSigner.value.first_name || ""
        }`
      : currentSigner.value.first_name || t("Другой участник");
    throw new Error(
      t("Сейчас не ваша очередь. Ожидается подпись от: {name}", {
        name,
      })
    );
  }
  if (currentSigner.value.status === "signed") {
    throw new Error(t("Вы уже подписали этот документ"));
  }
  if (currentSigner.value.status === "rejected") {
    throw new Error(t("Вы отклонили этот документ"));
  }
  return true;
}

async function handleSign(signatureData, algorithm) {
  if (!participantUuid.value) {
    signError.value = t("Не найден UUID участника");
    proxy?.$message?.error(signError.value);
    return;
  }

  try {
    validateSigningOrder();
  } catch (error) {
    signError.value = error.message;
    proxy?.$message?.error(signError.value);
    return;
  }

  signError.value = "";
  isSuccessSign.value = false;
  isSubmitting.value = true;

  try {
    const signParams = {
      participant_uuid: participantUuid.value,
      algorithm: algorithm || "SHA256",
      signature_data: signatureData,
      ip_address: await getClientIP(),
      user_agent: getUserAgent(),
      device_fingerprint: generateDeviceFingerprint(),
    };

    const result = await signParticipant(transaction_id.value, signParams);

    if (result.success && result.payload) {
      await loadCurrentSigner();
      if (result.payload?.signature_uuid) {
        await doSignatureValidate(result.payload?.signature_uuid);
      }
      isSuccessSign.value = true;
      emit("signed", result);

      setTimeout(() => {
        closeSignFlow();
      }, 2000);
    }
  } catch (error) {
    console.error("Ошибка при подписании:", error);
    handleSignError(error);
    await loadCurrentSigner();
  } finally {
    isSubmitting.value = false;
  }
}

function handleSignError(error) {
  if (error.response?.status === 403) {
    const errorCode = error.response.data?.code;
    if (
      errorCode === "NOT_YOUR_TURN" ||
      error.response.data?.message?.includes("очередь")
    ) {
      signError.value = t("Сейчас не ваша очередь подписания");
    } else {
      signError.value = t("У вас нет прав для подписания этого документа");
    }
  } else if (error.response?.status === 400) {
    if (signMethod.value === "hand") {
      signError.value = t("Неверный формат изображения подписи");
    } else if (signMethod.value === "eds") {
      signError.value = t("Неверный формат ЭЦП подписи");
    } else {
      signError.value = error.response.data?.message || t("Ошибка валидации");
    }
  } else {
    signError.value =
      error.response?.data?.message || t("Ошибка при подписании документа");
  }
  proxy?.$message?.error(signError.value);
  isSuccessSign.value = false;
}

async function doSignatureValidate(signature_uuid) {
  try {
    const data = await signatureValidate(signature_uuid);
    if (data.success && data.payload) {
      // proxy?.$message?.warning(data.payload);
      return data.payload;
    }
  } catch (error) {
    console.error("Ошибка валидация signature:", error);
  }
}

async function handleSignClick() {
  if (!props.consentChecked) {
    proxy?.$message?.warning(t("Сначала подтвердите согласие с документом"));
    return;
  }

  await loadCurrentSigner();

  try {
    validateSigningOrder();
  } catch (error) {
    proxy?.$message?.error(error.message);
    return;
  }

  if (isMobile.value && signMethod.value === "hand") {
    isSuccessSign.value = false;
    signError.value = "";
    showSignFlow.value = true;
    return;
  }

  if (!isMobile.value && signMethod.value === "hand") {
    proxy?.$message?.info(
      t("Подпись рукой доступна только на мобильных устройствах")
    );
    return;
  }

  if (signMethod.value === "eds") {
    await signWithEDS();
    return;
  }

  proxy?.$message?.info(t("Данный способ скоро будет доступен"));
}

async function signWithEDS() {
  try {
    if (!participantUuid.value) {
      throw new Error("Не найден UUID участника");
    }

    try {
      validateSigningOrder();
    } catch (error) {
      proxy?.$message?.error(error.message);
      return;
    }

    isSubmitting.value = true;
    signError.value = "";

    const transactionHash =
      currentSignerData.value?.transaction_hash || String(transaction_id.value);

    const base64Data = btoa(unescape(encodeURIComponent(transactionHash)));
    const result = await nclayerPort.sign(base64Data);

    if (result.signatures?.length > 0) {
      const signature = result.signatures[0];
      signMethod.value = "eds";
      await handleSign(signature);
    } else {
      throw new Error("Не получена подпись от NCALayer");
    }
  } catch (error) {
    console.error("Ошибка подписания ЭЦП:", error);
    signError.value = error.message || t("Ошибка при подписании ЭЦП");
    proxy?.$message?.error(signError.value);
  } finally {
    isSubmitting.value = false;
  }
}

function closeSignFlow() {
  showSignFlow.value = false;
  isSuccessSign.value = false;
  signError.value = "";
  isSubmitting.value = false;
  emit("close");
}

function goToHome() {
  router.push({ name: "client" });
}

onMounted(() => {
  loadCurrentSigner();
});
</script>

<template>
  <div class="template">
    <div class="block">
      <div class="sign-body">
        <div v-if="isLoadingSigner" class="loading-info">
          {{ $t("Загрузка информации о подписанте...") }}
        </div>

        <div class="signer-info">
          <div class="signer-card">
            <div v-if="currentSigner" class="signatures-progress">
              {{ $t("Сейчас подписывает:") }}
              <strong class="text-sm pl-1">
                {{ currentSigner.last_name }} {{ currentSigner.first_name }}
              </strong>
            </div>
            <div
              v-if="currentSigner && !canCurrentUserSign"
              class="not-turn-message"
            >
              {{ $t("Сейчас не ваша очередь") }}
            </div>

            <div class="signatures-progress">
              {{ signaturesStatusText }}
            </div>
            <div
              v-if="signaturesInfo.remaining === 0"
              class="text-xs text-green-600"
            >
              {{ $t("Вы и все участники подписали этот документ") }}
            </div>
            <div
              v-else-if="hasUserAlreadySigned"
              class="text-xs text-green-600"
            >
              {{ $t("Вы уже подписали этот документ") }}
            </div>
          </div>
        </div>

        <template v-if="!hasUserAlreadySigned">
          <div>
            <div class="subtitle">
              {{ $t("Выберите способ") }}
            </div>
          </div>

          <div class="sign-options">
            <button
              :class="['sign-option', { active: signMethod === 'eds' }]"
              @click="signMethod = 'eds'"
            >
              <div class="flex-1 flex flex-col gap-1">
                <div class="option-title">{{ $t("Использовать ЭЦП") }}</div>
                <div class="option-subtitle">
                  {{ $t("Подписать документ файлом электронной подписи") }}
                </div>
              </div>
              <div
                class="radio-dot"
                :class="{ checked: signMethod === 'eds' }"
              ></div>
            </button>

            <button
              v-if="isMobile"
              :class="['sign-option', { active: signMethod === 'hand' }]"
              @click="signMethod = 'hand'"
            >
              <div class="flex-1 flex flex-col gap-1">
                <div class="option-title">{{ $t("Подпись рукой") }}</div>
                <div class="option-subtitle">
                  {{ $t("На следующем экране распишитесь от руки") }}
                </div>
              </div>
              <div
                class="radio-dot"
                :class="{ checked: signMethod === 'hand' }"
              ></div>
            </button>
          </div>

          <div class="flex" :class="isMobile ? '' : 'justify-end'">
            <Button
              variant="default"
              class="action-button"
              :class="isMobile ? 'w-full' : 'w-[200px]'"
              @click="handleSignClick"
              :disabled="
                !consentChecked ||
                isSubmitting ||
                !canCurrentUserSign ||
                isLoadingSigner ||
                hasUserAlreadySigned
              "
            >
              {{ $t("Подписать") }}
            </Button>
          </div>
        </template>
        <template v-else>
          <div class="flex" :class="isMobile ? '' : 'justify-end'">
            <Button
              variant="default"
              class="action-button"
              :class="isMobile ? 'w-full' : 'w-[200px]'"
              @click="goToHome"
            >
              {{ $t("Вернуться на главную") }}
            </Button>
          </div>
        </template>
      </div>
    </div>
  </div>

  <ClientDocumentSignFlow
    :visible="showSignFlow"
    :participant-uuid="participantUuid"
    :is-submitting="isSubmitting"
    :is-success-sign="isSuccessSign"
    :sign-error="signError"
    @close="closeSignFlow"
    @sign="handleSign"
  />
</template>

<style scoped lang="scss">
.template {
  @apply flex h-full w-full overflow-x-hidden gap-5;

  .block {
    @apply flex flex-col w-1/2 h-full overflow-hidden;
    min-height: 600px;
    background-color: hsl(var(--background));
    border: 1px solid hsl(var(--border));
    border-radius: 16px;
  }
}

@media (max-width: 900px) {
  .template {
    @apply flex flex-col;

    .block {
      @apply w-full;
      min-height: auto;
    }

    .sign-body {
      @apply flex-1 flex flex-col gap-4 px-4 py-6;

      .subtitle {
        @apply text-start text-foreground text-base font-bold;
      }

      .sign-options {
        @apply flex flex-col gap-3;
      }

      .sign-option {
        @apply w-full px-4 py-3 rounded-md border border-border;
        @apply flex flex-row items-center gap-3 text-left;
        background-color: hsl(var(--background));
      }

      .sign-option.active {
        background-color: hsl(var(--muted));
      }

      .option-title {
        @apply text-sm font-semibold text-foreground;
      }

      .option-subtitle {
        @apply text-xs text-muted-foreground;
      }

      .option-badge {
        @apply text-xs text-muted-foreground;
        @apply px-1.5 py-0.5 rounded-md;
        background-color: hsl(var(--muted));
      }
    }
  }
}

.sign-body {
  @apply w-full h-full overflow-auto flex flex-col gap-6 p-6;
}

.signer-info {
  @apply flex flex-col gap-3 mb-2;
}

.signer-card {
  @apply p-4 rounded-md flex flex-col gap-1.5;
  background-color: hsl(var(--muted));
  border: 1px solid hsl(var(--border));
}

.signatures-progress {
  @apply text-xs text-muted-foreground;
}

.loading-info {
  @apply text-sm text-muted-foreground p-4 text-center;
}

.subtitle {
  font-size: 14px;
  font-weight: 600;
  color: hsl(var(--foreground));
}

.sign-options {
  @apply flex flex-col gap-4;
}

.sign-option {
  @apply w-full rounded-md border;
  @apply bg-card px-4 py-3 text-left;
  @apply flex flex-row items-center gap-4;
  border-color: hsl(var(--border));
  background-color: hsl(var(--background));
  transition: all 0.15s ease;

  &:hover {
    background-color: hsl(var(--muted));
  }

  &.active {
    background-color: hsl(var(--muted));
  }
}

.option-title {
  font-size: 14px;
  font-weight: 500;
  color: hsl(var(--foreground));
}

.option-subtitle {
  font-size: 13px;
  color: hsl(var(--muted-foreground));
}

.option-badge {
  font-size: 12px;
  color: hsl(var(--muted-foreground));
}

.radio-dot {
  @apply w-5 h-5 rounded-full flex-shrink-0;
  border: 2px solid hsl(var(--border));
  transition: all 0.15s ease;

  &.checked {
    border: 6px solid hsl(var(--foreground));
  }
}

.action-button {
  &:disabled {
    @apply opacity-50 cursor-not-allowed;
  }
}

.not-turn-message {
  @apply text-xs text-yellow-700;
}
</style>
